import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

type Question = {
  id: string;
  title: string;
  body: string;
  asker: { email: string; name: string };
  status: 'open' | 'answered' | 'closed';
  tags: string[];
  levelLink?: number;
  upvotes: number;
  replyCount: number;
  acceptedAnswer: null | { body: string; answerer: { name: string; email: string }; answeredAt: string };
  createdAt: string;
};

type Reply = {
  id: string;
  title: string;
  body: string;
  answerer: { name: string; email: string };
  createdAt: string;
};

type DetailResponse = { question: Question; replies: Reply[] };

const formatRelative = (iso: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  const minutesAgo = Math.floor((Date.now() - d.getTime()) / 60000);
  if (minutesAgo < 1) return 'just now';
  if (minutesAgo < 60) return `${minutesAgo} min ago`;
  const hoursAgo = Math.floor(minutesAgo / 60);
  if (hoursAgo < 24) return `${hoursAgo}h ago`;
  const daysAgo = Math.floor(hoursAgo / 24);
  return `${daysAgo}d ago`;
};

export function QADetail() {
  const { id } = useParams();
  const { state } = useAuth();
  const qc = useQueryClient();
  const [replyText, setReplyText] = useState('');
  const [postError, setPostError] = useState<string | null>(null);

  const detailQuery = useQuery<DetailResponse>({
    queryKey: ['qa-detail', id],
    queryFn: () => api.get<DetailResponse>(`/api/qa/${id}`),
    enabled: !!id,
  });

  const replyMutation = useMutation({
    mutationFn: (body: string) => api.post<{ id: string }>(`/api/qa/${id}/reply`, { body }),
    onSuccess: () => {
      setReplyText('');
      setPostError(null);
      qc.invalidateQueries({ queryKey: ['qa-detail', id] });
      qc.invalidateQueries({ queryKey: ['qa'] }); // list page replyCount
    },
    onError: (err) => {
      setPostError(err instanceof ApiError ? err.message : 'Could not post reply.');
    },
  });

  const upvoteMutation = useMutation({
    mutationFn: () => api.post<{ ok: boolean; upvotes: number; upvoted: boolean }>(`/api/qa/${id}/upvote`),
    onSuccess: (data) => {
      qc.setQueryData<DetailResponse>(['qa-detail', id], (prev) => prev
        ? { ...prev, question: { ...prev.question, upvotes: data.upvotes } }
        : prev);
      qc.invalidateQueries({ queryKey: ['qa'] });
    },
  });

  const acceptMutation = useMutation({
    mutationFn: (replyId: string) => api.post<{ ok: true }>(`/api/qa/${id}/accept`, { replyId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['qa-detail', id] });
      qc.invalidateQueries({ queryKey: ['qa'] });
    },
  });

  if (detailQuery.isLoading) {
    return <div style={{ padding: 48, textAlign: 'center', color: 'var(--color-text-3)' }}>Loading…</div>;
  }
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '64px 32px' }}>
        <p style={{ color: 'var(--color-danger)' }}>This question couldn't be loaded.</p>
        <Link className="btn" to="/qa">← Back to Q&A</Link>
      </div>
    );
  }

  const { question, replies } = detailQuery.data;
  const currentUserEmail = state.status === 'signed-in' ? state.user.email.toLowerCase() : null;
  const isAsker = currentUserEmail !== null && currentUserEmail === question.asker.email.toLowerCase();
  const canAccept = isAsker && question.status !== 'answered';

  return (
    <div className="view" style={{ maxWidth: 880, margin: '0 auto', padding: '36px 32px 96px' }}>
      <div style={{ marginBottom: 22 }}>
        <Link
          to="/qa"
          style={{ fontSize: 12, color: 'var(--color-text-3)', textDecoration: 'none' }}
        >
          ← Q&A
        </Link>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr', gap: 22, alignItems: 'start' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            padding: '12px 8px',
            borderRadius: 10,
            background: 'var(--color-bg-card)',
            border: '1px solid var(--color-border)',
          }}
        >
          <button
            onClick={() => upvoteMutation.mutate()}
            disabled={upvoteMutation.isPending}
            style={{
              background: 'transparent',
              border: 0,
              cursor: upvoteMutation.isPending ? 'wait' : 'pointer',
              color: 'var(--color-text-3)',
              fontSize: 14,
              padding: 4,
            }}
            aria-label="Upvote question"
            title="Upvote"
          >
            ▲
          </button>
          <span style={{ fontWeight: 700, fontSize: 18 }}>{question.upvotes}</span>
          <span style={{ color: 'var(--color-text-3)', fontSize: 10 }}>votes</span>
        </div>

        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <span
              className={question.status === 'answered' ? 'chip chip-accent' : 'chip chip-warn'}
              style={{ fontSize: 10 }}
            >
              {question.status === 'answered' ? '✓ Answered' : question.status === 'open' ? '🟠 Open' : '⚫ Closed'}
            </span>
            {question.tags.map((t) => (
              <span key={t} className="chip" style={{ fontSize: 10 }}>{t}</span>
            ))}
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 14px' }}>
            {question.title}
          </h1>
          {question.body && (
            <p style={{ color: 'var(--color-text-2)', fontSize: 14.5, lineHeight: 1.7, margin: '0 0 18px', whiteSpace: 'pre-wrap' }}>
              {question.body}
            </p>
          )}
          <div style={{ fontSize: 11.5, color: 'var(--color-text-3)' }}>
            Asked by {question.asker.name || question.asker.email || 'someone'} · {formatRelative(question.createdAt)}
          </div>
        </div>
      </div>

      {question.acceptedAnswer && (
        <div
          style={{
            marginTop: 32,
            padding: '20px 22px',
            borderRadius: 10,
            background: 'var(--color-accent-soft)',
            border: '1px solid rgba(79,233,181,0.32)',
          }}
        >
          <div className="eyebrow" style={{ color: 'var(--color-accent)' }}>▌ Accepted answer</div>
          <p style={{ color: 'var(--color-text)', fontSize: 14, lineHeight: 1.65, margin: '8px 0 6px', whiteSpace: 'pre-wrap' }}>
            {question.acceptedAnswer.body}
          </p>
          <div style={{ fontSize: 11.5, color: 'var(--color-text-3)' }}>
            {question.acceptedAnswer.answerer.name || question.acceptedAnswer.answerer.email || 'someone'} ·{' '}
            {formatRelative(question.acceptedAnswer.answeredAt)}
          </div>
        </div>
      )}

      <h3
        style={{
          marginTop: 36,
          marginBottom: 14,
          fontSize: 14,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--color-text-3)',
          fontWeight: 600,
        }}
      >
        {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
      </h3>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {replies.map((r) => (
          <div
            key={r.id}
            style={{
              padding: '16px 20px',
              borderRadius: 10,
              background: 'var(--color-bg-card)',
              border: '1px solid var(--color-border)',
            }}
          >
            <p style={{ color: 'var(--color-text)', fontSize: 14, lineHeight: 1.65, margin: '0 0 10px', whiteSpace: 'pre-wrap' }}>
              {r.body || <em style={{ color: 'var(--color-text-3)' }}>(no body)</em>}
            </p>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <span style={{ fontSize: 11.5, color: 'var(--color-text-3)' }}>
                {r.answerer.name || r.answerer.email || 'someone'} · {formatRelative(r.createdAt)}
              </span>
              {canAccept && (
                <button
                  onClick={() => acceptMutation.mutate(r.id)}
                  disabled={acceptMutation.isPending}
                  className="btn btn-tiny"
                  style={{ borderColor: 'rgba(79,233,181,0.32)', color: 'var(--color-accent)' }}
                >
                  ✓ Accept this answer
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          marginTop: 32,
          padding: '20px 22px',
          borderRadius: 10,
          background: 'var(--color-bg-card)',
          border: '1px solid var(--color-border)',
        }}
      >
        <div className="eyebrow" style={{ marginBottom: 8 }}>▌ Post a reply</div>
        <textarea
          rows={4}
          maxLength={5000}
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          placeholder="What's worked for you? Be specific."
          style={{
            width: '100%',
            padding: '10px 12px',
            background: 'var(--color-bg-deep)',
            border: '1px solid var(--color-border-strong)',
            borderRadius: 6,
            color: 'var(--color-text)',
            fontSize: 13,
            fontFamily: 'inherit',
            resize: 'vertical',
            marginBottom: 8,
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: postError ? 'var(--color-danger)' : 'var(--color-text-3)' }}>
            {postError ?? `${replyText.length} / 5000 characters`}
          </span>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!replyText.trim() || replyMutation.isPending}
            onClick={() => {
              if (replyText.trim()) replyMutation.mutate(replyText.trim());
            }}
          >
            {replyMutation.isPending ? 'Posting…' : '▸ Post reply'}
          </button>
        </div>
      </div>
    </div>
  );
}
